/**
 * One room in the results.
 *
 * A room that cannot be booked renders disabled and says why — it is never a
 * bookable-looking card that fails on submit.
 */
import { Icon } from "@/components/Icon";
import { StatusPill } from "@/components/StatusPill";
import { formatTime } from "@/lib/format";
import type { SearchRoom } from "@/lib/types";

const AMENITY_ICONS: Record<string, Parameters<typeof Icon>[0]["name"]> = {
  "Video conferencing": "video",
  Display: "tablet",
  Whiteboard: "grid",
  Phone: "phone",
  "Natural light": "sun",
};

export function RoomCard({
  room,
  onBook,
  onOpen,
  window,
}: {
  room: SearchRoom;
  onBook?: () => void;
  onOpen: () => void;
  window: { start: Date; end: Date };
}) {
  const tone = room.available
    ? room.requires_approval
      ? "soon"
      : "available"
    : "occupied";
  const label = room.available
    ? room.requires_approval
      ? "Needs approval"
      : "Free"
    : "Not available";

  const dayStart = new Date(window.start);
  dayStart.setHours(7, 0, 0, 0);
  const dayEnd = new Date(window.start);
  dayEnd.setHours(20, 0, 0, 0);
  const span = dayEnd.getTime() - dayStart.getTime();

  return (
    <article
      className={`room-card${room.available ? "" : " room-card--unavailable"}`}
      aria-label={`${room.name}, ${label}`}
    >
      <div className="room-card__head">
        <div className="stack" style={{ gap: 2, minWidth: 0 }}>
          <button
            type="button"
            className="room-card__name truncate"
            onClick={onOpen}
            style={{
              background: "none", border: "none", padding: 0, cursor: "pointer",
              textAlign: "left", color: "inherit", font: "inherit",
            }}
          >
            {room.name}
          </button>
          <span className="room-card__meta">
            {room.capacity} seats · {room.floor ?? room.location_name}
            {room.floor && room.location_name ? ` · ${room.location_name}` : ""}
          </span>
        </div>
        <StatusPill tone={tone}>{label}</StatusPill>
      </div>

      {room.amenities.length > 0 && (
        <div className="room-card__amenities">
          {room.amenities.slice(0, 4).map((amenity) => (
            <span className="amenity" key={amenity}>
              <Icon name={AMENITY_ICONS[amenity] ?? "check"} size={13} />
              {amenity}
            </span>
          ))}
        </div>
      )}

      {/* The day at a glance: where this room is busy, and where the request sits. */}
      <div className="stack" style={{ gap: 6 }}>
        <div className="timeline" aria-hidden="true">
          {(() => {
            const segments: React.ReactNode[] = [];
            let cursor = dayStart.getTime();
            const sorted = [...room.schedule].sort(
              (a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime(),
            );
            sorted.forEach((slot, index) => {
              const slotStart = Math.max(new Date(slot.start_at).getTime(), dayStart.getTime());
              const slotEnd = Math.min(new Date(slot.end_at).getTime(), dayEnd.getTime());
              if (slotEnd <= cursor) return;
              if (slotStart > cursor) {
                segments.push(
                  <span key={`free-${index}`} className="timeline__block timeline__block--free"
                    style={{ width: `${((slotStart - cursor) / span) * 100}%` }} />,
                );
              }
              segments.push(
                <span key={`busy-${index}`}
                  className={`timeline__block timeline__block--${slot.own ? "own" : "busy"}`}
                  style={{ width: `${((slotEnd - slotStart) / span) * 100}%` }} />,
              );
              cursor = slotEnd;
            });
            return segments;
          })()}
        </div>
        <div className="timeline-legend">
          <span>07:00</span>
          <span className="grow" />
          <span>
            <i style={{ background: "var(--status-occupied)", opacity: 0.55 }} />
            Booked
          </span>
          <span>
            <i style={{ background: "var(--accent)" }} />
            Yours
          </span>
          <span className="grow" />
          <span>20:00</span>
        </div>
      </div>

      <div className="room-card__foot">
        {room.available ? (
          <>
            <span className="t-caption ink-tertiary">
              {room.requires_approval ? room.approval_reason : "Free for your whole meeting"}
            </span>
            <button type="button" className="btn btn--primary btn--compact" onClick={onBook}>
              {room.requires_approval ? "Request" : "Book"}
            </button>
          </>
        ) : (
          <>
            <span className="room-card__reason">{room.unavailable_reason}</span>
            {room.next_free_at && (
              <span className="t-caption ink-tertiary" style={{ whiteSpace: "nowrap" }}>
                Free from {formatTime(room.next_free_at, room.timezone)}
              </span>
            )}
          </>
        )}
      </div>
    </article>
  );
}
